import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import './index.less';

import { GuardrailCheckStatus, GuardrailType } from '../../../../gamma-api';
import IbIcon from '../../common/IbIcon';
import IbTypography from '../../common/IbTypography';
import IbCollapse, { IbCollapsePanel } from '../../common/IbCollapse';
import MarkdownContent from '../../MarkdownContent';
import { agentMessageEventDataToMarkdown } from '../utils';
import {
  getGuardrailCheckStatus,
  getGuardrailCheckTitle,
  GuardrailCheckData,
  GuardrailEventData,
  isGuardrailCheckBlocked,
  isGuardrailCheckWarning,
} from '../guardrailChecks';

const MAIN_CLASS_NAME = 'guardrail-checks-event-details';
const SUMMARY_CLASS_NAME = `${MAIN_CLASS_NAME}__summary`;
const SUMMARY_ERROR_CLASS_NAME = `${SUMMARY_CLASS_NAME}_error`;
const SUMMARY_WARNING_CLASS_NAME = `${SUMMARY_CLASS_NAME}_warning`;
const SUMMARY_TITLE_CLASS_NAME = `${SUMMARY_CLASS_NAME}__title`;
const SUMMARY_ROW_CLASS_NAME = `${SUMMARY_CLASS_NAME}__row`;
const SUMMARY_STATUS_CLASS_NAME = `${SUMMARY_CLASS_NAME}__status`;
const SUMMARY_STATUS_ERROR_CLASS_NAME = `${SUMMARY_STATUS_CLASS_NAME}_error`;
const SUMMARY_STATUS_WARNING_CLASS_NAME = `${SUMMARY_STATUS_CLASS_NAME}_warning`;
const DETAILS_PANEL_CLASS_NAME = `${MAIN_CLASS_NAME}__details-panel`;
const INPUT_BLOCK_CLASS_NAME = `${MAIN_CLASS_NAME}__input-block`;
const INPUT_ROW_CLASS_NAME = `${INPUT_BLOCK_CLASS_NAME}__row`;

const TRANSLATE_KEY = 'Agent: Guardrail checks event details:';

const GUARDRAIL_CHECK_RESULT_DETAIL_KEYS = {
  stage: 'stage',
  checkResult: 'check_result',
  confidence: 'confidence',
  reason: 'reason',
  errorMessage: 'error_message',
} as const;

type GuardrailCheckDetailKey = typeof GUARDRAIL_CHECK_RESULT_DETAIL_KEYS[keyof typeof GUARDRAIL_CHECK_RESULT_DETAIL_KEYS];

const DEFAULT_GUARDRAIL_CHECK_DETAIL_KEYS: GuardrailCheckDetailKey[] = [
  GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.stage,
  GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.checkResult,
  GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.confidence,
  GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.reason,
  GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.errorMessage,
];

const GUARDRAIL_CHECK_DETAIL_KEYS_BY_TYPE: Partial<Record<GuardrailType, GuardrailCheckDetailKey[]>> = {
  [GuardrailType.CustomPromptCheck]: DEFAULT_GUARDRAIL_CHECK_DETAIL_KEYS,
  [GuardrailType.ExternalCheck]: DEFAULT_GUARDRAIL_CHECK_DETAIL_KEYS,
  [GuardrailType.RegexCheck]: [
    GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.stage,
    GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.checkResult,
    GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.reason,
    GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.errorMessage,
  ],
};

export interface IGuardrailChecksEventDetailsProps {
  guardrailData: GuardrailEventData;
}

interface IGuardrailCheckDetail {
  key: GuardrailCheckDetailKey;
  value: unknown;
}

const isEmptyDetailValue = (value: unknown): boolean => {
  return value === undefined || value === null || (typeof value === 'string' && !value.trim());
};

const getGuardrailCheckDetailValue = (data: GuardrailCheckData, key: GuardrailCheckDetailKey): unknown => {
  switch (key) {
    case GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.stage:
      return data.stage;

    case GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.checkResult:
      return getGuardrailCheckStatus(data);

    case GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.confidence:
      return data.confidence;

    case GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.reason:
      return data.reason?.trim();

    case GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.errorMessage:
      return getGuardrailCheckStatus(data) === GuardrailCheckStatus.Error ? data.error_message?.trim() : undefined;

    default:
      return undefined;
  }
};

const buildGuardrailCheckDetails = (data: GuardrailCheckData): IGuardrailCheckDetail[] => {
  const detailKeys = GUARDRAIL_CHECK_DETAIL_KEYS_BY_TYPE[data.guardrail_type] || DEFAULT_GUARDRAIL_CHECK_DETAIL_KEYS;
  return detailKeys
    .map((key) => ({ key, value: getGuardrailCheckDetailValue(data, key) }))
    .filter(({ value }) => !isEmptyDetailValue(value));
};

const getGuardrailCheckKey = (data: GuardrailCheckData, index: number): string =>
  [String(index), data.stage, data.check_id, data.check_target].filter(Boolean).join(':');

const GuardrailChecksEventDetails: React.FC<IGuardrailChecksEventDetailsProps> = ({ guardrailData }) => {
  const { t } = useTranslation();
  const guardrailChecks = guardrailData.checks || [];
  const guardrailCheckEntries = guardrailChecks.map((check, index) => ({
    check,
    key: getGuardrailCheckKey(check, index),
  }));
  const hasError = guardrailChecks.some(isGuardrailCheckBlocked);
  const hasWarning = !hasError && guardrailChecks.some(isGuardrailCheckWarning);
  const guardrailCheckKeys = guardrailCheckEntries.map((entry) => entry.key);
  const guardrailCheckKeysKey = guardrailCheckKeys.join('\n');
  const [activeGuardrailCheckKeys, setActiveGuardrailCheckKeys] = useState<string[]>(guardrailCheckKeys);
  const summaryClasses = [SUMMARY_CLASS_NAME];
  if (hasError) {
    summaryClasses.push(SUMMARY_ERROR_CLASS_NAME);
  }
  if (hasWarning) {
    summaryClasses.push(SUMMARY_WARNING_CLASS_NAME);
  }

  const translateDetail = (key: string) => t(`${TRANSLATE_KEY} ${key}`);

  useEffect(() => {
    setActiveGuardrailCheckKeys(guardrailCheckKeysKey ? guardrailCheckKeysKey.split('\n') : []);
  }, [guardrailCheckKeysKey]);

  const onCollapseChange = (keys: string | string[]) => {
    setActiveGuardrailCheckKeys(Array.isArray(keys) ? keys : keys ? [keys] : []);
  };

  const renderExpandIcon = ({ isActive }: { isActive: boolean }) => (
    <IbIcon iconName={isActive ? 'down' : 'up'} size={20} />
  );

  const renderDetailValue = (key: GuardrailCheckDetailKey, value: unknown) => {
    if (key === GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.checkResult) {
      return translateDetail(`Check result: ${value}`);
    }

    if (key === GUARDRAIL_CHECK_RESULT_DETAIL_KEYS.stage) {
      return translateDetail(`Stage: ${value}`);
    }

    if (typeof value === 'boolean') {
      return value ? translateDetail('Yes') : translateDetail('No');
    }

    if (typeof value === 'object') {
      return <MarkdownContent>{agentMessageEventDataToMarkdown(value)}</MarkdownContent>;
    }

    return String(value);
  };

  const renderGuardrailCheckDetails = (data: GuardrailCheckData) => {
    const details = buildGuardrailCheckDetails(data);

    return (
      <div className={INPUT_BLOCK_CLASS_NAME}>
        {details.map(({ key, value }) => (
          <div key={key} className={INPUT_ROW_CLASS_NAME}>
            <IbTypography.Paragraph disabled strong type="secondary">
              {translateDetail(key)}
            </IbTypography.Paragraph>
            <IbTypography.Paragraph type="secondary">{renderDetailValue(key, value)}</IbTypography.Paragraph>
          </div>
        ))}
      </div>
    );
  };

  const renderGuardrailCheckSummaryRow = (check: GuardrailCheckData) => {
    const isBlocked = isGuardrailCheckBlocked(check);
    const isWarning = !isBlocked && isGuardrailCheckWarning(check);

    return (
      <div className={SUMMARY_ROW_CLASS_NAME}>
        <IbIcon
          className={[
            SUMMARY_STATUS_CLASS_NAME,
            ...(isBlocked ? [SUMMARY_STATUS_ERROR_CLASS_NAME] : []),
            ...(isWarning ? [SUMMARY_STATUS_WARNING_CLASS_NAME] : []),
          ].join(' ')}
          iconName={isBlocked || isWarning ? 'attention' : 'check-one'}
          size={16}
        />
        <IbTypography.Paragraph type="secondary">{getGuardrailCheckTitle(check)}</IbTypography.Paragraph>
      </div>
    );
  };

  return (
    <>
      <div className={summaryClasses.join(' ')}>
        <div className={SUMMARY_TITLE_CLASS_NAME}>
          <IbIcon iconName="detection" size={20} />
          <IbTypography.Paragraph strong>{t('Agent: Guardrail checks event: Title')}</IbTypography.Paragraph>
        </div>
        {guardrailCheckEntries.map(({ check, key }) => (
          <React.Fragment key={key}>{renderGuardrailCheckSummaryRow(check)}</React.Fragment>
        ))}
      </div>
      <IbCollapse
        accordion={false}
        activeKey={activeGuardrailCheckKeys}
        className={MAIN_CLASS_NAME}
        expandIcon={renderExpandIcon}
        expandIconPosition="right"
        onChange={onCollapseChange}
      >
        {guardrailCheckEntries.map(({ check, key }) => (
          <IbCollapsePanel
            key={key}
            className={DETAILS_PANEL_CLASS_NAME}
            header={renderGuardrailCheckSummaryRow(check)}
          >
            {renderGuardrailCheckDetails(check)}
          </IbCollapsePanel>
        ))}
      </IbCollapse>
    </>
  );
};

export default GuardrailChecksEventDetails;
